import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { EventCard, EventCardDensity, StatusTag } from '@cogneed/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayType } from '../../../../../../../../../generated/core-api/graphql-react-query';
import useClockTime from '../../../../../../../../../Hooks/useClockTime';
import { sanguine, ultraMarine } from '../../../../../../../../../utils/colors';
import useDisplay from '../../../../../../../../Contexts/GlobalContext/Hooks/useDisplay';
import useIsAppEnabled from '../../../../../../../../LiveContext/ApplicationSyncContext/useIsAppEnabled';
import useTimedHighlight from '../../../../../../../../Hooks/useTimedHighlight';
import { DetectionGroup } from '../../../../../../Contexts/DetectionGroupContext';
import DetectionLine from './Components/DetectionListItem';
import {
  CommandsCountText, MetaText, MoreText, TitleText,
} from './styles';

const SHOWN_COMMANDS_COUNT = 3;

// "+N autres commandes" -- a count of the remaining commands beyond the 3
// shown. Its own click target (stopping propagation so it doesn't also
// trigger the card's onClick) expands the group in place to reveal the rest.
function DetectionGroupMore(props: { count: number, onClick: () => void }) {
  const { t } = useTranslation();
  const { count, onClick } = props;
  return (
    <MoreText
      component="button"
      type="button"
      onClick={(event: React.MouseEvent) => { event.stopPropagation(); onClick(); }}
    >
      {t('detections.moreCommands', { count })}
    </MoreText>
  );
}

function DetectionGroupBody(props: { detectionGroup: DetectionGroup, index: number, density: EventCardDensity }) {
  const { detectionGroup, index, density } = props;
  const isHighlighted = useTimedHighlight(detectionGroup.detections[0].date) && index === 0;
  const [expanded, setExpanded] = React.useState(false);
  const shown = expanded ? detectionGroup.detections : detectionGroup.detections.slice(0, SHOWN_COMMANDS_COUNT);
  return (
    <>
      {shown.map((detection, detectionIndex) => (
        <DetectionLine key={detection.id} detection={detection} isHighlighted={isHighlighted && detectionIndex === 0} density={density} />
      ))}
      {!expanded && detectionGroup.detections.length > SHOWN_COMMANDS_COUNT && (
        <DetectionGroupMore count={detectionGroup.detections.length - SHOWN_COMMANDS_COUNT} onClick={() => setExpanded(true)} />
      )}
    </>
  );
}

interface DetectionGroupListItemProps {
  detectionGroup: DetectionGroup;
  index: number;
  density?: EventCardDensity;
}

// Detection variant of the shared EventCard shell (see @cogneed/design-system) --
// the same shell will back the decision-tree run cards and the compliance
// checklist items once those are migrated too.
export default function DetectionGroupListItem(props: DetectionGroupListItemProps) {
  const { detectionGroup, index, density = 'panel' } = props;
  const { t } = useTranslation();
  const display = useDisplay();
  const isAppEnabled = useIsAppEnabled();
  const clockTime = useClockTime(detectionGroup.lastOccurrence);
  const isFromClient = detectionGroup.detections[0]?.isFromClient;

  // "Nouvelle détection" stays up for at least 5s (a near-instant flip was
  // barely perceptible) -- but a newer group taking over the top slot
  // (index 0) downgrades this one to plain "Détection" immediately, even if
  // the 5s haven't elapsed yet.
  const isRecent = useTimedHighlight(detectionGroup.lastOccurrence, 5000);
  const isUnseen = index === 0 && isRecent;
  const badge = (
    <StatusTag
      color={isUnseen ? 'primary' : 'default'}
      label={isUnseen ? t('detections.new') : t('detections.detection')}
    />
  );

  return (
    <EventCard
      badge={badge}
      density={density}
      meta={<MetaText density={density}>{clockTime}</MetaText>}
      onClick={() => { display(detectionGroup.id, DisplayType.History); }}
      title={(
        <>
          {isAppEnabled && (
            <GraphicEqIcon style={{ fontSize: 14, marginTop: 2, color: isFromClient ? sanguine[500] : ultraMarine[500] }} />
          )}
          <TitleText density={density}>{detectionGroup.title}</TitleText>
          {density === 'page' && detectionGroup.detections.length > 1 && (
            <CommandsCountText>
              {`· ${t('detections.commandsCount', { count: detectionGroup.detections.length })}`}
            </CommandsCountText>
          )}
        </>
      )}
    >
      <DetectionGroupBody detectionGroup={detectionGroup} index={index} density={density} />
    </EventCard>
  );
}
